import React, { useCallback, useEffect, useRef, useState } from "react";
import TappableArabic from "./TappableArabic";
import { shortName } from "../utils/shortName";

/** Strip Arabic diacritics (harakat, dagger alif, tatweel) for display only. */
export function stripHarakat(text) {
  return (text || "").replace(/[\u064B-\u065F\u0670\u0640]/g, "");
}

// Canonical home is utils/shortName.js; re-exported so existing
// `import { shortName } from "./CallChat"` call sites keep working.
export { shortName };

/**
 * WhatsApp-style transcript for a practice call. Shared across the live in-call
 * panel, the end-of-call review, and the past-calls viewer.
 *
 * Props:
 *  - messages: [{ role: "me"|"partner", name?, arabic, translation, ts }]
 *  - partnerName?: string — fallback label for incoming bubbles (msg.name wins)
 *  - myName?: string — fallback label for outgoing bubbles
 *  - showHarakat?: bool — when false, harakat are stripped from displayed Arabic
 *  - onSaveMessage?: async (msg) => void — when provided, the bubble action row
 *      gets a Save control plus a "Save all to Word Bank" button
 *  - onCorrectMessage?: async (index, msg) => void — when provided, bubbles get
 *      a "Translate" action that asks the AI to fix the STT + add harakat &
 *      translation
 *  - onSuggestReply?: (msg) => void — when provided, partner bubbles get a
 *      "Suggest reply" action (auto-suggest fires ONLY from this tap)
 *  - compact?: bool — tighter spacing for the in-call panel
 *  - fill?: bool — let the list flex to the parent's height (in-call layout)
 *  - emptyText?: string — shown when there are no messages yet
 *  - tappableWords?: bool — when false, Arabic renders as plain text instead
 *      of TappableArabic (whose Word Bank popover needs an authenticated
 *      user; the anonymous onboarding call sim turns this off)
 */
export default function CallChat({
  messages = [],
  partnerName,
  myName,
  showHarakat = true,
  onSaveMessage,
  onCorrectMessage,
  onSuggestReply,
  compact = false,
  fill = false,
  emptyText,
  tappableWords = true,
}) {
  const scrollRef = useRef(null);
  const [savedKeys, setSavedKeys] = useState(() => new Set());
  const [savingKey, setSavingKey] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [fixingIdx, setFixingIdx] = useState(null);
  const [fixError, setFixError] = useState("");
  const [openIdx, setOpenIdx] = useState(null); // bubble whose action row is revealed

  const fixOne = useCallback(async (m, i) => {
    if (!onCorrectMessage || fixingIdx !== null) return;
    setFixingIdx(i);
    setFixError("");
    try {
      await onCorrectMessage(i, m);
    } catch (e) {
      setFixError(e?.message || "Couldn't translate that line.");
    } finally {
      setFixingIdx((x) => (x === i ? null : x));
    }
  }, [onCorrectMessage, fixingIdx]);

  const keyOf = (m, i) => `${i}:${m.arabic}`;

  // Auto-scroll to the newest message.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const saveOne = useCallback(async (m, key) => {
    if (!onSaveMessage || savedKeys.has(key) || savingKey === key) return;
    setSavingKey(key);
    try {
      await onSaveMessage(m);
      setSavedKeys((prev) => new Set(prev).add(key));
    } catch {
      /* surfaced by caller if needed */
    } finally {
      setSavingKey((k) => (k === key ? null : k));
    }
  }, [onSaveMessage, savedKeys, savingKey]);

  const saveAll = useCallback(async () => {
    if (!onSaveMessage || savingAll) return;
    setSavingAll(true);
    const next = new Set(savedKeys);
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i];
      const key = keyOf(m, i);
      if (next.has(key)) continue;
      try {
        await onSaveMessage(m);
        next.add(key);
        setSavedKeys(new Set(next));
      } catch {
        /* skip failures, keep going */
      }
    }
    setSavingAll(false);
  }, [onSaveMessage, messages, savedKeys, savingAll]);

  const allSaved = messages.length > 0 && messages.every((m, i) => savedKeys.has(keyOf(m, i)));
  const hasActions = !!(onCorrectMessage || onSaveMessage || onSuggestReply);

  return (
    <div className={`flex flex-col min-h-0 ${fill ? "flex-1" : ""}`}>
      {onSaveMessage && messages.length > 0 && (
        <div className="flex justify-end pb-2 shrink-0">
          <button
            onClick={saveAll}
            disabled={savingAll || allSaved}
            className="text-xs px-3 py-1.5 rounded-full bg-dt-gold-bright text-dt-ink font-semibold hover:brightness-105 disabled:opacity-40 transition-[filter]"
          >
            {allSaved ? "All saved ✓" : savingAll ? "Saving…" : "Save all to Word Bank"}
          </button>
        </div>
      )}

      {fixError && (
        <p className="text-xs mb-2 shrink-0" style={{ color: "var(--tag-rose)" }}>{fixError}</p>
      )}

      <div
        ref={scrollRef}
        className={`flex-1 min-h-0 overflow-y-auto flex flex-col gap-2.5 px-1.5 pt-1 pb-2 ${
          compact && !fill ? "max-h-44 sm:max-h-56" : ""
        }`}
      >
        {messages.length === 0 ? (
          <p className="text-xs py-6 text-center" style={{ color: "var(--parchment-dim)" }}>
            {emptyText || "No messages yet."}
          </p>
        ) : (
          messages.map((m, i) => {
            const mine = m.role === "me";
            const key = keyOf(m, i);
            const saved = savedKeys.has(key);
            const name = shortName(m.name || (mine ? myName : partnerName)) || (mine ? "You" : "Partner");
            const open = openIdx === i;
            return (
              <div key={key} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                <p className="text-[10px] font-semibold mb-0.5 px-1" style={{ color: "var(--gold-dim)" }}>
                  {name}
                </p>
                <div className={`flex items-end gap-1.5 max-w-[88%] ${mine ? "flex-row-reverse" : ""}`}>
                  <div
                    className={`rounded-2xl px-3.5 py-2 ${mine ? "rounded-br-md" : "surface-glass rounded-bl-md"}`}
                    style={
                      mine
                        ? { background: "var(--forest-softer)", border: "1px solid var(--panel-line)" }
                        : undefined
                    }
                  >
                    {tappableWords ? (
                      <TappableArabic
                        text={showHarakat ? m.arabic : stripHarakat(m.arabic)}
                        className="font-arabic text-lg leading-loose"
                        style={{ color: "var(--parchment)" }}
                      />
                    ) : (
                      <p dir="rtl" lang="ar" className="font-arabic text-lg leading-loose" style={{ color: "var(--parchment)" }}>
                        {showHarakat ? m.arabic : stripHarakat(m.arabic)}
                      </p>
                    )}
                    {m.translation && (
                      <p className="text-xs mt-0.5" style={{ color: "var(--parchment-dim)" }}>
                        {m.translation}
                      </p>
                    )}
                  </div>
                  {hasActions && (
                    <button
                      onClick={() => setOpenIdx(open ? null : i)}
                      title="Translate / suggest a reply"
                      aria-label="Message actions"
                      aria-expanded={open}
                      className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs transition-colors"
                      style={{
                        border: "1px solid var(--panel-line)",
                        color: open ? "var(--gold-bright)" : "var(--parchment-dim)",
                        background: open ? "var(--gold-bg)" : "transparent",
                      }}
                    >
                      ✦
                    </button>
                  )}
                </div>
                {open && hasActions && (
                  <div className={`flex items-center gap-2 mt-1 ${mine ? "flex-row-reverse" : ""}`}>
                    {onCorrectMessage && (
                      <button
                        onClick={() => fixOne(m, i)}
                        disabled={fixingIdx !== null}
                        title="Use AI to fix the transcription, add harakat & translation"
                        className="text-[11px] px-2.5 py-1 rounded-full dt-ghost-btn disabled:opacity-50"
                      >
                        {fixingIdx === i ? "Translating…" : "Translate"}
                      </button>
                    )}
                    {onSuggestReply && !mine && (
                      <button
                        onClick={() => { onSuggestReply(m); setOpenIdx(null); }}
                        title="Suggest fuṣḥā replies to this line"
                        className="text-[11px] px-2.5 py-1 rounded-full dt-ghost-btn"
                      >
                        💡 Suggest reply
                      </button>
                    )}
                    {onSaveMessage && (
                      <button
                        onClick={() => saveOne(m, key)}
                        disabled={saved || savingKey === key}
                        className="text-[11px] px-2.5 py-1 rounded-full dt-ghost-btn disabled:opacity-50"
                      >
                        {saved ? "Saved ✓" : savingKey === key ? "Saving…" : "Save"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
